import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { transactionsApi, billsApi, categoriesApi } from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { Transaction, Bill, Category } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Search, Unlink, Receipt } from 'lucide-react'

interface LinkToBillDialogProps {
  transaction: Transaction | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function LinkToBillDialog({ transaction, open, onOpenChange }: LinkToBillDialogProps) {
  const queryClient = useQueryClient()
  const [billSearch, setBillSearch] = useState('')
  const [tab, setTab] = useState<string>('existing')

  const isLinked = !!transaction?.linked_bill_id

  // Fetch bills for selection
  const { data: bills = [] } = useQuery<Bill[]>({
    queryKey: ['bills'],
    queryFn: async () => {
      const res = await billsApi.getAll()
      return res.data
    },
    enabled: open && !isLinked,
  })

  // Fetch categories for new bill form
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['categories', 'bill'],
    queryFn: async () => {
      const res = await categoriesApi.getAll('bill')
      return res.data
    },
    enabled: open && tab === 'create',
  })

  const linkBill = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { bill_id?: number; new_bill?: any } }) =>
      transactionsApi.linkBill(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      onOpenChange(false)
    },
  })

  const unlinkBill = useMutation({
    mutationFn: (id: number) => transactionsApi.unlinkBill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      onOpenChange(false)
    },
  })

  const filteredBills = bills.filter(bill =>
    bill.name.toLowerCase().includes(billSearch.toLowerCase())
  )

  const handleSelectBill = (billId: number) => {
    if (!transaction) return
    linkBill.mutate({ id: transaction.id, data: { bill_id: billId } })
  }

  const handleCreateBill = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!transaction) return
    const formData = new FormData(e.currentTarget)
    linkBill.mutate({
      id: transaction.id,
      data: {
        new_bill: {
          name: formData.get('name') as string,
          amount: parseFloat(formData.get('amount') as string),
          due_day: parseInt(formData.get('due_day') as string),
          frequency: formData.get('frequency') as string,
          category_id: formData.get('category_id') ? parseInt(formData.get('category_id') as string) : undefined,
        }
      }
    })
  }

  const handleUnlink = () => {
    if (!transaction) return
    unlinkBill.mutate(transaction.id)
  }

  if (!transaction) return null

  // Extract day from transaction date for pre-fill
  const transactionDay = new Date(transaction.date).getDate()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Link to Bill
          </DialogTitle>
          <DialogDescription>
            {transaction.merchant} &mdash; {formatCurrency(Math.abs(transaction.amount))}
          </DialogDescription>
        </DialogHeader>

        {isLinked ? (
          <div className="space-y-4">
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Currently linked to:</p>
              <p className="text-lg font-semibold">{transaction.linked_bill_name}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button
                variant="destructive"
                onClick={handleUnlink}
                disabled={unlinkBill.isPending}
              >
                <Unlink className="mr-2 h-4 w-4" />
                {unlinkBill.isPending ? 'Unlinking...' : 'Unlink Bill'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="existing">Existing Bill</TabsTrigger>
              <TabsTrigger value="create">Create New</TabsTrigger>
            </TabsList>

            <TabsContent value="existing" className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search bills..."
                  value={billSearch}
                  onChange={(e) => setBillSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {filteredBills.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {bills.length === 0 ? 'No bills found. Create one instead.' : 'No matching bills.'}
                  </p>
                ) : (
                  filteredBills.map(bill => (
                    <button
                      key={bill.id}
                      onClick={() => handleSelectBill(bill.id)}
                      disabled={linkBill.isPending}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium text-sm">{bill.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {bill.category_name || 'Uncategorized'} &middot; Due day {bill.due_day}
                        </p>
                      </div>
                      <span className="text-sm font-medium">{formatCurrency(bill.amount)}</span>
                    </button>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="create">
              <form onSubmit={handleCreateBill} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bill-name">Bill Name</Label>
                  <Input
                    id="bill-name"
                    name="name"
                    defaultValue={transaction.merchant}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bill-amount">Amount</Label>
                    <Input
                      id="bill-amount"
                      name="amount"
                      type="number"
                      step="0.01"
                      defaultValue={Math.abs(transaction.amount).toFixed(2)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bill-due-day">Due Day</Label>
                    <Input
                      id="bill-due-day"
                      name="due_day"
                      type="number"
                      min="1"
                      max="31"
                      defaultValue={transactionDay}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bill-frequency">Frequency</Label>
                    <Select name="frequency" defaultValue="monthly">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Biweekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="semiannual">Semi-annual</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bill-category">Category</Label>
                    <Select name="category_id">
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id.toString()}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={linkBill.isPending}>
                    {linkBill.isPending ? 'Creating...' : 'Create & Link'}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}
